// ConferenceRegistration.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { 
  getAllConferences, 
  registerForConference, 
  seedDefaultConferences 
} from '../api/conferences';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import './ConferenceRegistration.css';

const ConferenceRegistration = () => {
  const { user, userData } = useContext(AuthContext);
  
  // State
  const [conferences, setConferences] = useState([]);
  const [loadingConferences, setLoadingConferences] = useState(true);
  const [selectedConference, setSelectedConference] = useState(null);
  
  const [formData, setFormData] = useState({
    fullNameArabic: '',
    fullNameEnglish: '',
    jobTitle: '',
    workplace: '',
    phone: '',
    email: '',
    isMember: 'Yes',
    previouslyRepresented: 'No',
    conferenceId: '',
    researchTitle: '',
  });
  
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Load conferences
  useEffect(() => {
    fetchConferences();
  }, []);

  // Autofill form when userData is loaded
  useEffect(() => {
    if (userData) {
      const nameEnglish = `${userData.title || ''} ${userData.firstName || ''} ${userData.lastName || ''}`.trim();
      setFormData(prev => ({
        ...prev,
        fullNameEnglish: nameEnglish,
        phone: userData.phone || '',
        email: userData.email || '',
        jobTitle: userData.designation || '',
        workplace: userData.workplace || '',
        isMember: 'Yes',
      }));
    }
  }, [userData]);

  const fetchConferences = async () => {
    try {
      setLoadingConferences(true);
      let data = await getAllConferences();
      
      // If empty, auto-seed default conferences so there is instant data
      if (data.length === 0) {
        await seedDefaultConferences();
        data = await getAllConferences();
      }
      
      const activeConfs = data.filter(c => c.active !== false);
      setConferences(activeConfs);
      
      if (activeConfs.length > 0) {
        // Select first conference by default
        setSelectedConference(activeConfs[0]);
        setFormData(prev => ({
          ...prev,
          conferenceId: activeConfs[0].id
        }));
      }
    } catch (err) {
      console.error('Error fetching conferences:', err);
      setError('Failed to load available conferences. Please refresh.');
    } finally {
      setLoadingConferences(false);
    }
  };

  const handleConferenceChange = (e) => {
    const confId = e.target.value;
    const conf = conferences.find(c => c.id === confId);
    setSelectedConference(conf);
    setFormData(prev => ({
      ...prev,
      conferenceId: confId
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Only PDF documents are allowed.');
        setPdfFile(null);
        e.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be smaller than 10MB.');
        setPdfFile(null);
        e.target.value = '';
        return;
      }
      setPdfFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!formData.fullNameArabic || !formData.fullNameEnglish || !formData.jobTitle || 
        !formData.workplace || !formData.phone || !formData.email || !formData.conferenceId || 
        !formData.researchTitle || !pdfFile) {
      setError('Please fill in all required fields and upload your proof of abstract acceptance PDF.');
      return;
    }

    try {
      setSubmitting(true);
      const result = await registerForConference(user.uid, formData, pdfFile);
      setSuccessData(result);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error in registration submission:', err);
      setError(err.message || 'An error occurred during registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper check if the selected conference is already full (Reserve mode)
  const isSelectedConfFull = () => {
    if (!selectedConference) return false;
    const sponsored = parseInt(selectedConference.sponsoredSlots) || 0;
    const registered = parseInt(selectedConference.primaryRegisteredCount) || 0;
    return registered >= sponsored;
  };

  if (loadingConferences) {
    return (
      <div className="registration-container loading-container">
        <div className="loading-spinner"></div>
        <p>Loading active conferences...</p>
      </div>
    );
  }

  // Success view
  if (successData) {
    const isPrimary = successData.status === 'primary';
    return (
      <div className="registration-container">
        <div className="success-card">
          <div className="success-icon">{isPrimary ? '✨' : '📝'}</div>
          <h2>Registration Submitted!</h2>
          <p className="success-subtitle">
            Thank you for applying. Your registration has been processed successfully.
          </p>

          <div className="application-summary">
            <h3>Application Summary</h3>
            <div className="summary-row">
              <span>Conference:</span>
              <strong>{successData.conferenceName}</strong>
            </div>
            <div className="summary-row">
              <span>Applicant Name:</span>
              <strong>{successData.fullNameEnglish}</strong>
            </div>
            <div className="summary-row">
              <span>Status:</span>
              <span className={`status-badge ${successData.status}`}>
                {successData.status === 'primary' ? 'Primary' : 'Reserve (Waiting List)'}
              </span>
            </div>
          </div>

          {isPrimary ? (
            <div className="status-notice primary-notice">
              <span className="notice-icon">🎉</span>
              <p>
                Congratulations! You have been registered as a <strong>Primary</strong> applicant for this conference. Your sponsored slot is secured pending validation of your abstract acceptance proof by the administration.
              </p>
            </div>
          ) : (
            <div className="status-notice reserve-notice">
              <span className="notice-icon">⚠️</span>
              <p>
                "The sponsored slots have been filled. You have been placed on the reserve list. If a selected applicant withdraws or does not meet the eligibility requirements, reserve applicants will be considered in the order of registration."
              </p>
            </div>
          )}

          <div className="success-actions">
            <Button variant="primary" onClick={() => {
              setSuccessData(null);
              setPdfFile(null);
              // re-fetch to update local slot counts
              fetchConferences();
            }}>
              Register Another Application
            </Button>
            <a href="/" className="back-home-link">Back to Home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-container">
      <div className="registration-header-section">
        <h1>Scientific Conference Applications</h1>
        <p>Apply for association sponsorship to represent us at leading medical ophthalmology conferences.</p>
      </div>

      <div className="registration-grid">
        {/* Left column: Information & conferences details */}
        <div className="info-column">
          <div className="glass-card conference-info-card">
            <h2>Available Conferences</h2>
            {conferences.length === 0 ? (
              <p className="no-confs">No active conferences are open for registration at the moment.</p>
            ) : (
              <div className="confs-list">
                {conferences.map(conf => {
                  const slots = parseInt(conf.sponsoredSlots) || 0;
                  const registered = parseInt(conf.primaryRegisteredCount) || 0;
                  const isFull = registered >= slots;
                  const percent = Math.min(100, (registered / slots) * 100);

                  return (
                    <div 
                      key={conf.id} 
                      className={`conf-info-item ${selectedConference?.id === conf.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedConference(conf);
                        setFormData(prev => ({ ...prev, conferenceId: conf.id }));
                      }}
                    >
                      <div className="conf-header">
                        <h3>{conf.name}</h3>
                        <span className={`pill ${isFull ? 'full' : 'available'}`}>
                          {isFull ? 'Reserve List' : 'Slots Open'}
                        </span>
                      </div>
                      
                      <div className="conf-details">
                        <p><strong>📅 Date:</strong> {conf.dateLabel || conf.date}</p>
                        <p><strong>📍 Location:</strong> {conf.location || 'N/A'}</p>
                        <p><strong>🎟️ Sponsored Slots:</strong> {slots}</p>
                      </div>

                      <div className="capacity-bar-wrapper">
                        <div className="capacity-labels">
                          <span>Slots filled: {registered}/{slots}</span>
                          <span>{isFull ? 'Waiting List' : `${slots - registered} left`}</span>
                        </div>
                        <div className="capacity-bar-bg">
                          <div 
                            className={`capacity-bar-fill ${isFull ? 'full' : ''}`}
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="glass-card instructions-card">
            <h3>Registration Rules</h3>
            <ul>
              <li>Sponsorship covers registration fees, subject to administrative approval.</li>
              <li>You must upload a valid abstract or research acceptance letter (PDF).</li>
              <li>Primary slots are allocated on a first-come, first-served basis.</li>
              <li>Once primary slots are filled, applicants are automatically placed on the reserve waiting list.</li>
            </ul>
          </div>
        </div>

        {/* Right column: Form */}
        <div className="form-column">
          <div className="glass-card registration-form-card">
            <h2>Registration Form</h2>
            
            {error && <div className="form-alert alert-error">{error}</div>}

            <form onSubmit={handleSubmit} className="registration-form">
              {/* Conference Selection */}
              <div className="form-group">
                <label htmlFor="conferenceId">Conference Selection *</label>
                <select
                  id="conferenceId"
                  name="conferenceId"
                  value={formData.conferenceId}
                  onChange={handleConferenceChange}
                  required
                >
                  <option value="" disabled>Select a conference</option>
                  {conferences.map(conf => (
                    <option key={conf.id} value={conf.id}>
                      {conf.name} ({conf.dateLabel || conf.date})
                    </option>
                  ))}
                </select>
              </div>

              {/* Show warning pre-submission if capacity is reached */}
              {isSelectedConfFull() && (
                <div className="form-notice reserve-notice-pre">
                  <span className="notice-icon">ℹ️</span>
                  <p>
                    <strong>Notice:</strong> Sponsored slots for this conference are filled. You will be registered on the <strong>Reserve (Waiting List)</strong>.
                  </p>
                </div>
              )}

              {/* Personal Info Grid */}
              <div className="form-row-grid">
                <div className="form-group">
                  <label htmlFor="fullNameArabic">Full Name (Arabic) *</label>
                  <Input
                    type="text"
                    id="fullNameArabic"
                    name="fullNameArabic"
                    placeholder="الاسم الكامل باللغة العربية"
                    value={formData.fullNameArabic}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="fullNameEnglish">Full Name (English, as in passport) *</label>
                  <Input
                    type="text"
                    id="fullNameEnglish"
                    name="fullNameEnglish"
                    placeholder="Full name as in passport"
                    value={formData.fullNameEnglish}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row-grid">
                <div className="form-group">
                  <label htmlFor="jobTitle">Current Job Title *</label>
                  <Input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    placeholder="e.g. Senior Registrar, Consultant"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="workplace">Current Workplace *</label>
                  <Input
                    type="text"
                    id="workplace"
                    name="workplace"
                    placeholder="e.g. Ibn Sina Hospital"
                    value={formData.workplace}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row-grid">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number (WhatsApp) *</label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="e.g. +965 9999 9999"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="e.g. name@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {/* Associations queries */}
              <div className="form-group">
                <label htmlFor="previouslyRepresented">Previously represented the association at a conference? *</label>
                <select
                  id="previouslyRepresented"
                  name="previouslyRepresented"
                  value={formData.previouslyRepresented}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Academic/Presentation */}
              <div className="form-group">
                <label htmlFor="researchTitle">Presentation / Research Title *</label>
                <Input
                  type="text"
                  id="researchTitle"
                  name="researchTitle"
                  placeholder="e.g. Efficacy of Anti-VEGF in Diabetic Retinopathy"
                  value={formData.researchTitle}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* PDF Proof Upload */}
              <div className="form-group">
                <label htmlFor="pdfFile">Proof of Abstract/Research Acceptance (PDF) *</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    id="pdfFile"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="file-input-hidden"
                    required
                  />
                  <div className={`file-upload-dropzone ${pdfFile ? 'has-file' : ''}`} onClick={() => document.getElementById('pdfFile').click()}>
                    <span className="upload-icon">📄</span>
                    {pdfFile ? (
                      <div className="file-info-label">
                        <span className="file-name">{pdfFile.name}</span>
                        <span className="file-size">({(pdfFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                      </div>
                    ) : (
                      <span className="upload-placeholder">Click to browse and upload abstract proof PDF (max 10MB)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="form-actions-row">
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={submitting}
                  className="submit-btn-full"
                >
                  {submitting ? 'Uploading Abstract & Submitting...' : (isSelectedConfFull() ? 'Submit (Join Waiting List)' : 'Submit Registration')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConferenceRegistration;
